import { useAppDispatch, useAppSelector } from "../../../hooks";
import { WebAppDeployTypes } from "../../../types/web-app";
import {
  handleChangeWebAppDeployType,
  handleClearWebAppCreationSlice,
  selectWebAppSelectedDeployType,
} from "../../../store/web-app/webAppCreateSlice";
import { useCallback } from "react";
import { cn, Tag, Typography } from "djuno-design";

export interface WebAppDeployOption {
  name: WebAppDeployTypes;
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  disabled?: boolean;
}

const deployTypes: WebAppDeployOption[] = [
  {
    name: "ai",
    title: (
      <div>
        Smart App <Tag color="success">beta</Tag>
      </div>
    ),
    description: "Ask djuno AI to develop and run a web app for you",
  },
  {
    name: "image",
    title: "Deploy an existing image from a registry",
    description:
      "Pull a public image from any registry or a private image from Docker Hub, GitHub, or GitLab.",
  },
  {
    name: "git",
    title: (
      <div>
        Build and deploy from a Git repository{" "}
        <Tag color="processing">developing</Tag>
      </div>
    ),
    description: "Connect a GitHub or GitLab repository.",
    disabled: import.meta.env.VITE_APP_SCOPE === "live",
  },
];

const DeployingTypeStep = () => {
  const selectedDeployType = useAppSelector(selectWebAppSelectedDeployType);

  const dispatch = useAppDispatch();

  const handleChangeDeployType = useCallback(
    (deployType: WebAppDeployTypes) => {
      dispatch(
        handleClearWebAppCreationSlice({
          withoutDeployType: true,
          withoutApplicationType: true,
        })
      );
      dispatch(handleChangeWebAppDeployType(deployType));
    },
    [dispatch]
  );

  return (
    <div className="max-w-3xl mx-auto pb-12 flex flex-col">
      <Typography.Text size="sm" className="font-medium">
        How would you like to deploy your web service?
      </Typography.Text>
      <div className="mt-4 w-full flex flex-col gap-4">
        {deployTypes.map((deployType, index) => (
          <DeployTypeCard
            key={index}
            {...deployType}
            selected={selectedDeployType === deployType.name}
            onClick={(name) =>
              handleChangeDeployType(name as WebAppDeployTypes)
            }
          />
        ))}
      </div>
    </div>
  );
};

export const DeployTypeCard: React.FC<{
  name: string;
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  disabled?: boolean;
  selected?: boolean;
  onClick?: (name: string) => void;
}> = ({ name, title, description, selected, onClick, disabled }) => {
  return (
    <div
      className={cn(
        "col-span-2 md:col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300",
        {
          "border-primary-400 dark:border-primary-400": selected,
          "cursor-pointer": onClick,
          "!cursor-not-allowed": disabled,
        }
      )}
      onClick={() => (onClick && !disabled ? onClick(name) : {})}
    >
      <div className="flex flex-col space-y-3 p-3 items-start justify-start">
        <Typography.Text size="sm" className="font-medium">
          {title}
        </Typography.Text>
        <Typography.Text size="sm" uiType="secondary">
          {description}
        </Typography.Text>
      </div>
    </div>
  );
};

export default DeployingTypeStep;
